import {
  ingestCotPillKey,
  INGEST_LEAD_PILL_KEY,
  type IngestCotByPill,
  type IngestCotPillTail,
  type IngestSection,
  type PendingIngestQuestion,
  type AnsweredIngestQuestion,
  type ProxyRunWithChildren
} from '@axilon-apps/domains-ontology-ui-state'

import { newestPendingQuestionId } from '../Chat/sessionTabRules'

export type IngestAgentPillStatus =
  | 'pending'
  | 'running'
  | 'waiting_user'
  | 'done'
  | 'failed'
  | 'aborted'

/** Alias used by the shared AgentPillStrip (ingest + orchestrator). */
export type AgentPillStatus = IngestAgentPillStatus

export interface IngestAgentPill {
  key: string
  label: string
  status: IngestAgentPillStatus
  dispatchId?: string
  sectionId?: string
  agentRunId?: string
  agentKind?: string
  /** One-line collapsed summary for completed non-selected pills. */
  summary?: string
}

export const LEAD_PILL_KEY = INGEST_LEAD_PILL_KEY

/**
 * Nested run error written when a child parks (HITL / Force). Must match
 * `DISPATCH_PARKED_WAITING_OPERATOR` in ontology-agents — duplicated here so
 * the browser package does not import the agent runtime.
 */
export const PARKED_WAITING_OPERATOR_ERROR = 'parked waiting for operator'

export function isParkedWaitingOperatorError(error: string | undefined | null): boolean {
  return error === PARKED_WAITING_OPERATOR_ERROR
}

/** Done / failed / aborted — free-text and Force must not target these children. */
export function isFinishedChildPillStatus(status: IngestAgentPillStatus): boolean {
  return status === 'done' || status === 'failed' || status === 'aborted'
}

/** Stable pill key for a catalog section — survives dispatch / nested-run lifecycle. */
export function sectionPillKey(sectionId: string): string {
  return `section:${sectionId}`
}

function runStatus(state: string | undefined, error?: string): IngestAgentPillStatus {
  switch (state) {
    case 'completed':
      return 'done'
    case 'failed':
      return 'failed'
    case 'aborted':
      // Park is a pause, not a closed dispatch — keep free-text / Force open.
      return isParkedWaitingOperatorError(error) ? 'waiting_user' : 'aborted'
    default:
      return 'running'
  }
}

/** Attribution tag for a nested ingest subagent run. */
export function runSourceLabel(run: ProxyRunWithChildren): string | undefined {
  if (run.agent_kind === 'ingest_section_parser') {
    const section = /Your section: ([^.\n]+)\./.exec(run.prompt ?? '')?.[1]
    return section ? `Section parser · ${section}` : 'Section parser'
  }
  if (run.agent_kind === 'ingest_reconciler') return 'Reconciler'
  return undefined
}

export function sectionIdFromRun(run: ProxyRunWithChildren): string | undefined {
  if (run.agent_kind !== 'ingest_section_parser') return undefined
  return /Your section: ([^.\n]+)\./.exec(run.prompt ?? '')?.[1]?.trim()
}

function runSummary(run: ProxyRunWithChildren): string | undefined {
  if (isParkedWaitingOperatorError(run.error)) {
    return 'Paused — waiting for operator'
  }
  const response = run.raw_response
  if (response && typeof response === 'object' && 'final_summary' in response) {
    const summary = (response as { final_summary?: unknown }).final_summary
    if (typeof summary === 'string' && summary.trim()) return summary.trim()
  }
  if (typeof run.error === 'string' && run.error.trim()) return run.error.trim()
  return undefined
}

function collectNestedRuns(transcript: readonly ProxyRunWithChildren[]): ProxyRunWithChildren[] {
  const out: ProxyRunWithChildren[] = []
  const walk = (runs: readonly ProxyRunWithChildren[]): void => {
    for (const run of runs) {
      if (run.agent_kind === 'ingest_section_parser' || run.agent_kind === 'ingest_reconciler') {
        out.push(run)
      }
      walk(run.children ?? [])
    }
  }
  walk(transcript)
  return out
}

function leadRunStatus(
  transcript: readonly ProxyRunWithChildren[],
  jobActive: boolean,
  jobEndStatus: 'done' | 'failed' | 'aborted'
): IngestAgentPillStatus {
  const leads = transcript.filter((r) => r.agent_kind === 'unstructured_ingest')
  const latest = leads[leads.length - 1]
  if (!latest) return jobActive ? 'running' : jobEndStatus
  const fromRun = runStatus(latest.state, latest.error)
  if (jobActive) return fromRun
  // Job detail can go terminal before the conversation poll lands a completed
  // lead run (polling stops with the job). Prefer an explicit terminal run;
  // otherwise trust the job end status so Lead does not spin forever.
  if (isFinishedChildPillStatus(fromRun)) return fromRun
  return jobEndStatus
}

function findPillBySection(
  pills: readonly IngestAgentPill[],
  sectionId: string | undefined
): IngestAgentPill | undefined {
  if (!sectionId) return undefined
  return pills.find((p) => p.sectionId === sectionId)
}

/**
 * Build the always-on Lead pill, one pill per active catalog section (even
 * before a parser starts), plus reconciler / live-dispatch pills that aren't
 * tied to a section. Final order is Lead → Reconciler → section pills → other.
 * Pending questions flip matching pills to `waiting_user`.
 */
export function buildIngestAgentPills(args: {
  transcript?: readonly ProxyRunWithChildren[]
  byPill?: IngestCotByPill
  pending?: readonly PendingIngestQuestion[]
  /** Active section catalog rows — each becomes a selectable pill immediately. */
  sections?: readonly Pick<IngestSection, 'id' | 'name' | 'status'>[]
  jobActive: boolean
  /**
   * When the job is terminal but a lead/child run is still non-terminal in the
   * cached transcript, map those stale statuses to this end state.
   */
  jobEndStatus?: 'done' | 'failed' | 'aborted'
}): IngestAgentPill[] {
  const transcript = args.transcript ?? []
  const byPill = args.byPill ?? {}
  const pending = args.pending ?? []
  const sections = (args.sections ?? []).filter((s) => s.status === 'active')
  const jobEndStatus = args.jobEndStatus ?? 'done'

  const pills: IngestAgentPill[] = [
    {
      key: LEAD_PILL_KEY,
      label: 'Lead',
      status: leadRunStatus(transcript, args.jobActive, jobEndStatus),
      agentKind: 'ingest_agent'
    }
  ]

  for (const section of sections) {
    pills.push({
      key: sectionPillKey(section.id),
      label: section.name.trim() || section.id,
      // Catalog-only until a dedicated parser (or live SSE) attaches. When the
      // job ends with no parser, the lead usually handled the section in-line.
      status: args.jobActive ? 'pending' : 'done',
      sectionId: section.id,
      agentKind: 'ingest_section_parser',
      ...(!args.jobActive ? { summary: 'Handled by lead (no dedicated parser)' } : {})
    })
  }

  const byRunId = new Map<string, IngestAgentPill>()
  for (const run of collectNestedRuns(transcript)) {
    const sectionId = sectionIdFromRun(run)
    const existing = findPillBySection(pills, sectionId)
    if (existing && run.agent_kind === 'ingest_section_parser') {
      existing.agentRunId = run.id
      existing.agentKind = run.agent_kind
      existing.status = runStatus(run.state, run.error)
      const summary = runSummary(run)
      if (summary) existing.summary = summary
      else delete existing.summary
      byRunId.set(run.id, existing)
      continue
    }

    const label = runSourceLabel(run) ?? 'Subagent'
    const pill: IngestAgentPill = {
      key: `run:${run.id}`,
      label,
      status: runStatus(run.state, run.error),
      agentRunId: run.id,
      agentKind: run.agent_kind,
      ...(sectionId ? { sectionId } : {}),
      ...(runSummary(run) ? { summary: runSummary(run) } : {})
    }
    pills.push(pill)
    byRunId.set(run.id, pill)
  }

  for (const [key, tail] of Object.entries(byPill)) {
    if (key === LEAD_PILL_KEY) continue

    if (tail.agentRunId && byRunId.has(tail.agentRunId)) {
      const existing = byRunId.get(tail.agentRunId)!
      if (tail.dispatchId) existing.dispatchId = tail.dispatchId
      if (tail.sectionId) existing.sectionId = tail.sectionId
      if (existing.status === 'done' || existing.status === 'failed') {
        if (tail.liveText.trim()) existing.status = 'running'
      } else if (tail.liveText.trim()) {
        // Including waiting_user: Force/park resume streams CoT on the same pill.
        existing.status = 'running'
      } else if (existing.status === 'pending') {
        existing.status = 'running'
      }
      continue
    }

    const bySection = findPillBySection(pills, tail.sectionId)
    if (bySection) {
      if (tail.dispatchId) bySection.dispatchId = tail.dispatchId
      if (tail.agentRunId) bySection.agentRunId = tail.agentRunId
      if (
        bySection.status === 'pending' ||
        bySection.status === 'done' ||
        bySection.status === 'failed' ||
        bySection.status === 'waiting_user' ||
        bySection.status === 'aborted'
      ) {
        if (tail.liveText.trim() || tail.agentRunId || tail.dispatchId) {
          bySection.status = 'running'
          if (bySection.summary === 'Handled by lead (no dedicated parser)') {
            delete bySection.summary
          }
        }
      } else if (tail.liveText.trim()) {
        bySection.status = 'running'
      }
      continue
    }

    if (pills.some((p) => p.key === key || (tail.dispatchId && p.dispatchId === tail.dispatchId))) {
      continue
    }

    pills.push({
      key,
      label: pillLabelFromTail(tail),
      status: 'running',
      dispatchId: tail.dispatchId,
      sectionId: tail.sectionId,
      agentRunId: tail.agentRunId,
      agentKind: tail.subagentKind
    })
  }

  for (const q of pending) {
    // Job is over — do not keep pills in waiting_user from a stale pending fetch.
    if (!args.jobActive) break
    const target = resolvePillKeyForQuestion(pills, q)
    const pill = pills.find((p) => p.key === target)
    if (pill) {
      pill.status = 'waiting_user'
      if (q.dispatchId) pill.dispatchId = q.dispatchId
      if (q.sectionId) pill.sectionId = q.sectionId
      if (q.agentRunId) pill.agentRunId = q.agentRunId
      continue
    }
    if (q.sectionId) {
      pills.push({
        key: sectionPillKey(q.sectionId),
        label: questionPillLabel(q),
        status: 'waiting_user',
        dispatchId: q.dispatchId,
        sectionId: q.sectionId,
        agentRunId: q.agentRunId,
        agentKind: q.agentKind
      })
      continue
    }
    if (q.dispatchId) {
      pills.push({
        key: ingestCotPillKey({ dispatchId: q.dispatchId }),
        label: questionPillLabel(q),
        status: 'waiting_user',
        dispatchId: q.dispatchId,
        sectionId: q.sectionId,
        agentRunId: q.agentRunId,
        agentKind: q.agentKind
      })
    } else {
      const lead = pills.find((p) => p.key === LEAD_PILL_KEY)
      if (lead) lead.status = 'waiting_user'
    }
  }

  if (!args.jobActive) {
    // Stale transcript / SSE tails can leave children (or Lead) as running after
    // the job row is already terminal — snap them to the job end status.
    for (const pill of pills) {
      if (
        pill.status === 'running' ||
        pill.status === 'waiting_user' ||
        pill.status === 'pending'
      ) {
        pill.status = jobEndStatus
      }
    }
  }

  return orderIngestAgentPills(pills)
}

/** Lead → Reconciler → catalog/section parsers → anything else. */
function orderIngestAgentPills(pills: readonly IngestAgentPill[]): IngestAgentPill[] {
  const lead: IngestAgentPill[] = []
  const reconciler: IngestAgentPill[] = []
  const sectionLike: IngestAgentPill[] = []
  const other: IngestAgentPill[] = []

  for (const pill of pills) {
    if (pill.key === LEAD_PILL_KEY) {
      lead.push(pill)
      continue
    }
    if (pill.agentKind === 'ingest_reconciler') {
      reconciler.push(pill)
      continue
    }
    if (pill.key.startsWith('section:') || pill.agentKind === 'ingest_section_parser') {
      sectionLike.push(pill)
      continue
    }
    other.push(pill)
  }

  return [...lead, ...reconciler, ...sectionLike, ...other]
}

function pillLabelFromTail(tail: IngestCotPillTail): string {
  if (tail.subagentKind === 'ingest_reconciler') return 'Reconciler'
  if (tail.subagentKind === 'ingest_section_parser') {
    return tail.sectionId ? `Section parser · ${tail.sectionId}` : 'Section parser'
  }
  return 'Subagent'
}

function questionPillLabel(q: PendingIngestQuestion): string {
  if (q.agentKind === 'ingest_reconciler') return 'Reconciler'
  if (q.sectionId) return `Section parser · ${q.sectionId}`
  if (q.agentKind === 'ingest_section_parser') return 'Section parser'
  return 'Subagent'
}

export function resolvePillKeyForQuestion(
  pills: readonly IngestAgentPill[],
  q: Pick<PendingIngestQuestion, 'dispatchId' | 'agentRunId' | 'agentKind' | 'sectionId'>
): string {
  if (q.agentRunId) {
    const byRun = pills.find((p) => p.agentRunId === q.agentRunId)
    if (byRun) return byRun.key
  }
  if (q.sectionId) {
    const bySection = findPillBySection(pills, q.sectionId)
    if (bySection) return bySection.key
    return sectionPillKey(q.sectionId)
  }
  if (q.dispatchId) {
    const byDispatch = pills.find((p) => p.dispatchId === q.dispatchId)
    if (byDispatch) return byDispatch.key
    return ingestCotPillKey({ dispatchId: q.dispatchId })
  }
  if (q.agentKind === 'ingest_section_parser' || q.agentKind === 'ingest_reconciler') {
    const byKind = pills.find((p) => p.agentKind === q.agentKind && p.status === 'waiting_user')
    if (byKind) return byKind.key
  }
  return LEAD_PILL_KEY
}

export function questionBelongsToPill(
  pills: readonly IngestAgentPill[],
  q: Pick<
    PendingIngestQuestion | AnsweredIngestQuestion,
    'dispatchId' | 'agentRunId' | 'agentKind' | 'sectionId'
  >,
  pillKey: string
): boolean {
  return resolvePillKeyForQuestion(pills, q) === pillKey
}

/** Whether an operator free-text message belongs on the selected pill. */
export function messageBelongsToPill(
  message: Pick<{ dispatchId?: string }, 'dispatchId'>,
  pillKey: string,
  pillDispatchId?: string
): boolean {
  if (pillKey === LEAD_PILL_KEY) return !message.dispatchId
  return Boolean(pillDispatchId && message.dispatchId === pillDispatchId)
}

/** Live SSE tail for the selected pill (lead, section, or matching dispatch/run). */
export function liveTailForPill(
  byPill: IngestCotByPill | undefined,
  pill: IngestAgentPill | undefined
): IngestCotPillTail | undefined {
  if (!byPill || !pill) return undefined
  if (pill.key === LEAD_PILL_KEY) return byPill[LEAD_PILL_KEY]
  if (pill.dispatchId) {
    const keyed = byPill[ingestCotPillKey({ dispatchId: pill.dispatchId })]
    if (keyed) return keyed
  }
  if (pill.sectionId) {
    const bySection = Object.values(byPill).find((t) => t.sectionId === pill.sectionId)
    if (bySection) return bySection
  }
  if (pill.agentRunId) {
    return Object.values(byPill).find((t) => t.agentRunId === pill.agentRunId)
  }
  return byPill[pill.key]
}

/**
 * Newest waiting pill key, if any — used to autoswitch focus when a child asks.
 */
export function newestWaitingPillKey(
  pills: readonly IngestAgentPill[],
  pending: readonly PendingIngestQuestion[]
): string | undefined {
  const writeId = newestPendingQuestionId(pending)
  if (!writeId) return undefined
  const newest = pending.find((q) => q.writeId === writeId)
  if (!newest) return undefined
  return resolvePillKeyForQuestion(pills, newest)
}
